import React from 'react';
import LocalTradeSelectionComponent from './LocalTradeSelection';
import { MechanicalContractorFormData, MechanicalContractorFormErrors, LocalTradeSelection } from './dialogTypes';

interface MechanicalContractorBasicInfoFormProps {
  formData: MechanicalContractorFormData;
  errors: MechanicalContractorFormErrors;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  setFormData: React.Dispatch<React.SetStateAction<MechanicalContractorFormData>>;
  isEditMode?: boolean;
  canDeactivate?: boolean;
  deactivationReason?: string;
  hideStatusToggle?: boolean;
}

const MechanicalContractorBasicInfoForm: React.FC<MechanicalContractorBasicInfoFormProps> = ({
  formData,
  errors,
  handleInputChange,
  setFormData,
  isEditMode = false,
  canDeactivate = true,
  deactivationReason = '',
  hideStatusToggle = false
}) => {
  const handleLocalTradeSelectionsChange = (selections: LocalTradeSelection[]) => {
    setFormData(prev => ({ ...prev, localTradeSelections: selections }));
  };

  return (
    <>
      <div className="pb-4">
        <label className="block text-small font-medium text-text-secondary mb-1">
          Contractor Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          id="input-name"
          placeholder="Enter mechanical contractor name"
          className={`w-full px-3 py-2 text-body border ${errors.name ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
          value={formData.name}
          onChange={handleInputChange}
        />
        {errors.name && (
          <p className="mt-1 text-xs text-red-500">{errors.name}</p>
        )}
      </div>

      {!hideStatusToggle && (
        <div className="pb-4">
          <label className="block text-small font-medium text-text-secondary mb-1">
            Status
          </label>
          <div
            className="flex items-center mt-2"
            title={isEditMode && formData.status === 'active' && !canDeactivate && deactivationReason ? `Cannot inactivate: ${deactivationReason}` : ''}
          >
            <div className="relative inline-flex items-center">
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.status === 'active'}
                  onChange={(e) => {
                    if (!e.target.checked && isEditMode && !canDeactivate) {
                      e.preventDefault();
                      return;
                    }
                    setFormData(prev => ({ ...prev, status: e.target.checked ? 'active' : 'inactive' }));
                  }}
                  disabled={isEditMode && formData.status === 'active' && !canDeactivate}
                  className="sr-only peer"
                  id="mechanical-contractor-status-toggle"
                />
                <div className={`
                  w-11 h-6 rounded-full
                  ${isEditMode && formData.status === 'active' && !canDeactivate
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-gray-200 peer-checked:bg-primary cursor-pointer'}
                  after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                  after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5
                  after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white
                `}></div>
              </label>
            </div>
            <span className="ml-2 text-sm">
              {formData.status.charAt(0).toUpperCase() + formData.status.slice(1)}
            </span>
          </div>
        </div>
      )}

      <LocalTradeSelectionComponent
        localTradeSelections={formData.localTradeSelections}
        onLocalTradeSelectionsChange={handleLocalTradeSelectionsChange}
        errors={errors}
      />
    </>
  );
};

export default MechanicalContractorBasicInfoForm;